import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    //Typography
} from "@mui/material";
import { GridSortModel, GridFilterModel } from "@mui/x-data-grid";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import LayoutAdmin from "../components/LayoutAdmin";
import axiosExt from '../axios';
import { Service } from "../types";
import { API_BASE_URL } from "../config.ts";
import { useTranslation } from "react-i18next";

const ServicesPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [items, setItems] = useState<Service[]>([]);
    const [open, setOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<Service | null>(null);
    //const [name, setName] = useState("");

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<string | null>(null);

    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

    const savedSortKey = "sortServicesList";
    const savedFilterKey = "filterServicesList";

    useEffect(() => {
        const savedSortModel = JSON.parse(localStorage.getItem(savedSortKey) ?? "[]");
        const savedFilterModel = (() => {
            try {
                const parsed = JSON.parse(localStorage.getItem(savedFilterKey) ?? "{}");
                return parsed && parsed.items ? parsed : { items: [] };
            } catch (error) {
                console.error("Error parsing savedFilterKey:", error);
                return { items: [] }; // Fallback
            }
        })();
        setSortModel(savedSortModel);
        setFilterModel(savedFilterModel);

        fetchItems();
    }, []);

    // Save sortModel changes to localStorage
    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
        localStorage.setItem(savedSortKey, JSON.stringify(newModel));
    };

    // Save filterModel changes to localStorage
    const handleFilterModelChange = (newModel: GridFilterModel) => {
        setFilterModel(newModel);
        localStorage.setItem(savedFilterKey, JSON.stringify(newModel));
    };

    const fetchItems = async () => {
        const token = localStorage.getItem("token");
        const response = await axiosExt.get(`${API_BASE_URL}/Services`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        setItems(response.data);
    };

    const handleSave = async () => {
        const token = localStorage.getItem("token");
        //const payload = { name };

        if (selectedItem?.id == null || selectedItem.id === '') {
            const payload: Service = {
                ...selectedItem, id: crypto.randomUUID(), name: selectedItem?.name ?? "", customPricingPerOrderDetails: selectedItem?.customPricingPerOrderDetails ?? false, additionalServiceID: selectedItem?.additionalServiceID ?? null 
            };

            //delete payload.id; // Remove the id field
            await axiosExt.post(`${API_BASE_URL}/Services`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
        } else {
            await axiosExt.put(`${API_BASE_URL}/Services/${selectedItem.id}`, selectedItem, {
                headers: { Authorization: `Bearer ${token}` },
            });
        }

        setOpen(false);
        fetchItems();
    };

    /*const handleDelete = async (id: number) => {
        const token = localStorage.getItem("token");
        await axiosExt.delete(`${API_BASE_URL}/Services/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
        });
        fetchItems();
    };*/

    const handleDelete = async (id: string) => {
        try {
            setItemToDelete(id);
            setConfirmDeleteOpen(true);
        } catch (error) {
            console.error("Error deleting email:", error);
        }
    };

    const handleConfirmDelete = async () => {
        if (itemToDelete === null) return;
        try {
            const token = localStorage.getItem("token");
            await axiosExt.delete(`${API_BASE_URL}/Services/${itemToDelete}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchItems();
        } catch (error) {
            console.error("Error deleting:", error);
        } finally {
            setConfirmDeleteOpen(false);
            setItemToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setConfirmDeleteOpen(false);
        setItemToDelete(null);
    };



    const handleEdit = (item: Service) => {
        setSelectedItem(item);
        setOpen(true);
    };

    const columns: GridColDef[] = [
        { field: "name", headerName: t("Name"), width: 150 },
        { field: "accountingNo", headerName: t("Accounting No"), width: 150 },
        { field: "customPricingPerOrderDetails", headerName: t("Custom Price/Order Detail"), width: 150 },
        {
            field: "additionalServiceID",
            headerName: t("Additional Service"),
            width: 220,
            valueGetter: (_value, row) => {
                const service = items.find(x => x.id === row.additionalServiceID);
                return service?.name ?? "";
            }
        },
        {
            field: "actions",
            headerName: t("Actions"),
            width: 250,
            renderCell: (params) => (
                <>
                    <Button variant="contained" onClick={() => handleEdit(params.row)} style={{ marginRight: 10 }}>{t("Edit")}</Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        style={{ marginRight: 10 }}
                        onClick={() =>
                            navigate(`/service-pricing?serviceId=${params.row.id}`)
                        }
                    >
                        Pricing
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => handleDelete(params.row.id)}
                        
                    >
                        {t("Delete")}
                    </Button>
                </>
            ),
        },
    ];

    return (
        <LayoutAdmin>
            <Box sx={{ width: "100%", display: "flex", flexDirection: "column", flexGrow: 1 }}>
                {/* Header section */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <h2 style={{ margin: 0 }}>{t("Services")}</h2>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => {
                            setSelectedItem({ id: "", name: "" , customPricingPerOrderDetails:false, accountingNo:"", additionalServiceID: null });
                            setOpen(true);
                        }}
                    >
                        {t("Add New")}
                    </Button>
                </Box>

                {/* Grid section */}
                <Box
                    sx={{
                        flexGrow: 1,
                        width: "100%",
                        height: "calc(100vh - 240px)",
                        "& .MuiDataGrid-root": {
                            border: "none",
                        },
                    }}
                >
                    <DataGrid
                        rows={items}
                        columns={columns}
                        sortModel={sortModel}
                        onSortModelChange={handleSortModelChange}
                        filterModel={filterModel}
                        onFilterModelChange={handleFilterModelChange}
                        getRowId={(r) => r.id}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        pageSizeOptions={[25,50, 100, 200]}
                    />
                </Box>

                {/* Add/Edit dialog */}
                <Dialog open={open} onClose={() => setOpen(false)}>
                    <DialogTitle>{selectedItem ? t("Edit") : t("Add")}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label={t("Name")}
                            fullWidth
                            value={selectedItem?.name}
                            onChange={(e) =>
                                setSelectedItem((prev) =>
                                    prev ? { ...prev, name: e.target.value } : null
                                )
                            }
                            margin="dense"
                        />
                        <TextField
                            label={t("Article No")}
                            fullWidth
                            value={selectedItem?.accountingNo}
                            onChange={(e) =>
                                setSelectedItem((prev) =>
                                    prev ? { ...prev, accountingNo: e.target.value } : null
                                )
                            }
                            margin="dense"
                        />
                        <Checkbox
                            checked={selectedItem?.customPricingPerOrderDetails ?? false}
                            onChange={(e) =>
                                setSelectedItem((prev) =>
                                    prev ? { ...prev, customPricingPerOrderDetails: e.target.checked } : null
                                )
                            }
                            id="customPricingPerOrderDetails"
                        />
                        <label htmlFor="customPricingPerOrderDetails" style={{ marginLeft: 8 }}>
                            {t("Custom Price/Order Detail")}
                        </label>
                        <FormControl fullWidth margin="dense">
                            <InputLabel id="additional-service-label">{t("Additional Service")}</InputLabel>
                            <Select
                                labelId="additional-service-label"
                                value={selectedItem?.additionalServiceID ?? ""}
                                label={t("Additional Service")}
                                onChange={(e) =>
                                    setSelectedItem((prev) =>
                                        prev
                                            ? {
                                                ...prev,
                                                additionalServiceID: e.target.value ? String(e.target.value) : null,
                                            }
                                            : null
                                    )
                                }
                            >
                                <MenuItem value="">
                                    <em>{t("None")}</em>
                                </MenuItem>

                                {items
                                    .filter(x => x.id !== selectedItem?.id)
                                    .map(service => (
                                        <MenuItem key={service.id!} value={service.id!}>
                                            {service.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
                        <Button onClick={handleSave}>{t("Save")}</Button>
                    </DialogActions>
                </Dialog>

                {/* Confirm Delete Dialog */}
                <Dialog open={confirmDeleteOpen} onClose={handleCancelDelete}>
                    <DialogTitle>{t("Confirm Delete")}</DialogTitle>
                    <DialogContent>
                        {t("Are you sure you want to delete this item?")}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelDelete}>{t("Cancel")}</Button>
                        <Button onClick={handleConfirmDelete} color="error">
                            {t("Delete")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </LayoutAdmin>
    );

};

export default ServicesPage;
